//@flow
import Q from 'q';
import _ from 'lodash';
// app
import { JSableOmitPrivate } from 'jsable';
import type { Constant, Observable, ObservableArray, PureComputed } from 'rgs-knockout';
import ko from 'rgs-knockout';
import type { IDataResponse } from 'app/rgs/api/request-result';
import type { Validator } from 'validation-builder';

// rgs-nfd-ui-kit
import {
	DEFAULT_DROPDOWN_PLACEHOLDER_TYPEAHEAD,
	DEFAULT_DROPDOWN_pathToID,
	DEFAULT_DROPDOWN_pathToName,
	DEFAULT_DROPDOWN_PLACEHOLDER,
} from 'rgs-nfd-ui-kit/ko-components/dropdown';
import type { TTextForms } from 'common-utils';
import { chooseWordCaseByNumeral } from 'common-utils';

export const DEFAULT_MAX_FOR_GET_ALL: number = 250;
export const DEFAULT_MIN_LEN_SEARCH_WORD: number = 3;
export const DEFAULT_SIZE_RESULT: number = 5;
export const DEFAULT_PLURALIZE: TTextForms = {
	nominative: 'шт',
	genitive: 'шт',
	genitive_plural: 'шт',
};

// TODO: проверить работу fetchSingleItem
export type TDropdownTypeaheadOptionsParams<TItem> = {
	fetch: (search: string, limitFetch: number) => Promise<{
		items: TItem[],
		total: number,
	}>,
	fetchSingleItem?: () => Promise<IDataResponse<TItem>>,
	pluralize?: TTextForms,
	customGetPluralize?: (maxCount: number, count: number)=>string,
	emptyPlaceholder?: string;
	placeholderTypeahead?: string;
	isDisabled?: Observable<boolean>;
	onCancel?: {
		visible?: Constant<boolean>,
		onClick?: () => any,
	};
	pathToID?: string;
	pathToName?: string;
	isOpened?: Observable<boolean>;
	allowOnlyName?: Observable<boolean>;
	minLengthSearchWord?: number;
	onItemClick?: () => void,
	onChange?: (item: TItem) => void,
	limitFetch?: number,
	maxHeight?: string,
	isFetchIfEmpty?: boolean,
	validation?: Validator,
	isFullInputValue?: boolean,
};

type TOptions<TItem> = {
	...TDropdownTypeaheadOptionsParams<TItem>,
	fetch: (search: ?string, limitFetch: number) => Promise<{
		items: TItem[],
		total: number,
	}>,
	onCancel: {
		visible: Constant<boolean>,
		onClick: () => any,
	};
	emptyPlaceholder: string;
	minLengthSearchWord: number,
	onItemClick: () => void,
	onChange: (item: ?TItem) => void,
	limitFetch: number,
	pluralize: TTextForms,
	customGetPluralize: false | (maxCount: number, count: number)=>string,
	emptyPlaceholder: string,
	isFetchIfEmpty: boolean,
	isFullInputValue: boolean,
};

export class DropdownTypeaheadOptionsExtended<TItem: Object, TItemID: any>
	extends JSableOmitPrivate {

	pathToName: string;
	pathToID: string;
	// TODO: Выпытать а Дани зачем это надо // state: options.state;
	isOpened: Observable<boolean>;
	isDisabled: Observable<boolean>;
	onCancel: {
		visible: Observable<boolean>,
		onClick: () => void,
	};

	primary: {
		isFetching: Observable<boolean>,
		inputValue: Observable<string>,
		items: ObservableArray<TItem>,
		allowOnlyName: Observable<boolean>,
	};
	parts: {
		onMoreButton: {
			visible: PureComputed<boolean>,
			onClick: () => void,
			text: PureComputed<string>,
		},
		onSearchLabel: {
			value: Observable<string>,
			onIconCloseClick: () => void,
		},
		onList: { style: { maxHeight: string } },
	};

	handlers: {
		onMapItemValue: (suggest: *) => string,
		onInputChange: (search: string) => void,
		onItemClick: (checkedItem: TItem) => void,
	};

	rest: {
		inputAttrs: { placeholder: string },
	};

	placeholder: { text: PureComputed<string> };

	_currentValue: Observable<?TItem> = ko.observable();
	_items: ObservableArray<TItem> = ko.observableArray().extend({ toFromJS: true });
	_total: Observable<number> = ko.observable(0).extend({ toFromJS: true });
	_inputValue: Observable<string> = ko.observable('').extend({ toFromJS: true });
	_lastSearchedValue: Observable<string> = ko.observable('').extend({ toFromJS: true });

	_options: TOptions<TItem>;

	constructor (options: TDropdownTypeaheadOptionsParams<TItem>) {
		super();
		this._options = _.defaultsDeep(options, {
			minLengthSearchWord: DEFAULT_MIN_LEN_SEARCH_WORD,
			limitFetch: DEFAULT_SIZE_RESULT,
			onItemClick: () => ({}),
			onCancel: {
				visible: ko.observable(true),
				onClick: () => undefined,
			},
			allowOnlyName: ko.observable(false),
			onChange: () => undefined,
			pluralize: DEFAULT_PLURALIZE,
			customGetPluralize: false,
			emptyPlaceholder: DEFAULT_DROPDOWN_PLACEHOLDER,
			isFetchIfEmpty: true,
		});

		this.activeValue = ko.pureComputed(() => {
			return Boolean(this.getCurrentID()) || Boolean(this.getCurrentName());
		});

		this.pathToName = _.get(options, 'pathToName', DEFAULT_DROPDOWN_pathToName);
		this.pathToID = _.get(options, 'pathToID', DEFAULT_DROPDOWN_pathToID);

		this.isDisabled = _.get(options, 'isDisabled', ko.observable(false));
		this.isOpened = _.get(options, 'isOpened', ko.observable(false));
		this.isFetching = _.get(options, 'isFetching', ko.observable(false));
		this.onCancel = {
			visible: this._options.onCancel.visible || ko.constant(true),
			onClick: () => {
				if (!this.isDisabled()) {
					this.clear();
					this._options.onCancel.onClick();
				}
			},
		};

		this.validationValue = this._currentValue;

		if (this._options.validation) {
			this._currentValue.extend({
				validation: this._options.validation,
			});
		}

		this.placeholder = {
			text: ko.pureComputed(() => {
				return this.getCurrentName() || this._options.emptyPlaceholder;
			}),
		};

		this.rest = {
			inputAttrs: {
				placeholder: _.get(
					options, 'placeholderTypeahead', DEFAULT_DROPDOWN_PLACEHOLDER_TYPEAHEAD,
				),
			},
		};

		this.primary = {
			isFetching: this.isFetching,
			inputValue: this._inputValue,
			items: this._items,
			allowOnlyName: this._options.allowOnlyName,
		};

		this.parts = {
			onMoreButton: {
				// TODO: onMoreButton
				visible: ko.pureComputed(() => this._total() > this._items().length),
				onClick: () => this._fetch(this._inputValue(), this._getCountForGetAll()),
				text: ko.pureComputed(() => (this._pluralize(this._total(), this._items().length))),
			},
			onSearchLabel: {
				value: this._lastSearchedValue,
				onIconCloseClick: () => {
					this.isOpened(false);
					this._inputValue('');
					this._lastSearchedValue('');
				},
			},
			onList: {
				style: {
					maxHeight: _.get(options, 'maxHeight', '250px'),
				},
			},
		};

		this.handlers = {
			onMapItemValue: (suggestItem: TItem): string => {
				return this.getItemName(suggestItem);
			},
			onInputChange: async (search: string) => {
				if (!_.isString(search)
				|| search.length
					? search.length < this._options.minLengthSearchWord
					: !this._options.isFetchIfEmpty
				) {
					return;
				}

				this._onSetValueByName(search);

				await this._fetch(search, this._options.limitFetch);
			},
			onItemClick: async (checkedItem: TItem) => {
				this.setCurrentValue(checkedItem);
				this.isOpened(false);

				this._options.onItemClick();
			},
		};

		this.init();
	}

	_onSetValueByName = (nameAnyValue: string) => {
		if (!ko.unwrap(this._options.allowOnlyName)) {
			return;
		}
		const name = this.getCurrentName();
		const id = this.getCurrentID();

		if (!_.isFunction(name)) {
			const newValue = {
				[this.pathToID]: null,
				[this.pathToName]: nameAnyValue,
			};
			this.setCurrentValue(newValue);
		} else {
			name(nameAnyValue);
			id(null);
		}
	};

	init = () => {
		if (this._options.isFetchIfEmpty) {
			this._fetch(this._inputValue());
		}
	};

	_fetch = async (search?: string, limitFetch?: number = DEFAULT_SIZE_RESULT) => {
		this.isFetching(true);
		Q(this._options.fetch(search, limitFetch)).then(({ items, total }) => {
			this._items(items);
			this._total(total);
		}).finally(() => {
			this.isFetching(false);
		}).done();
	};

	clear = async () => {
		this.setCurrentValue();
		await this._fetch();
	};

	refresh = async () => {
		this.init();
	};

	getCurrentValue = (): ?TItem => {
		return this._currentValue();
	};

	setCurrentValue = (item: ?TItem) => {

		this._currentValue(item);

		this._options.onChange(item);
	};

	getItemID = (item: ?TItem): TItemID => {
		return _.get(item, this.pathToID, '');
	};

	getItemName = (item: ?TItem): string => {
		return _.get(item, this.pathToName, '');
	};

	getCurrentID = (): TItemID => {
		return this.getItemID(this.getCurrentValue());
	};

	getCurrentName = (): string => {
		return this.getItemName(this.getCurrentValue());
	};

	_pluralize (maxCount: number, count: number): string {
		if (this._options.customGetPluralize) {
			return this._options.customGetPluralize();
		}

		const diff = Math.abs(maxCount - count);
		const unit = chooseWordCaseByNumeral(diff, this._options.pluralize);
		return `Показать еще ${diff} ${unit}`;
	}

	_getCountForGetAll (): number {
		const total = this._total();
		return total <= DEFAULT_MAX_FOR_GET_ALL ? total : DEFAULT_MAX_FOR_GET_ALL;
	}
}
